import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { DragEvent } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from '../components/toast.js';
import { api } from './api.js';

/**
 * Reihenfolge des Übergabe-Eingangs (#115, Task 4).
 *
 * Zieht Zustand + Ziehlogik aus `handover.tsx` heraus (die Seite hat schon ~1200
 * Zeilen) — Muster wie das Ziehen im Phase Board (`phase-board.tsx:1375-1440`):
 * eigener MIME-Typ, `dragId`/`dropTarget` als lokaler Zustand,
 * `dataTransfer.dropEffect = 'move'`, `e.preventDefault()` im `onDragOver`.
 *
 * KEINE optimistische Vertauschung: die Reihenfolge ist ein geteilter Zustand
 * (mehrere KL-Personen können gleichzeitig umsortieren) — die Server-Antwort ist
 * die Wahrheit. Ein Fehlschlag zeigt nur `toast.error`, der lokale Zustand bleibt
 * unverändert; erst die Invalidierung nach Erfolg holt die neue Reihenfolge.
 */

const DND_MIME = 'application/x-pv-uebergabe';

/** Ziel „ans Ende" — kein echtes Projekt, deshalb ein Wert, der nie als project_id vorkommt. */
export const REIHENFOLGE_ENDE_ZIEL = '__ende__';

export const UEBERGABE_REIHENFOLGE_QUERY_KEY = ['v2-uebergabe-reihenfolge'];

export type EingangReihenfolgeHandlers = {
  draggable: boolean;
  onDragStart: (e: DragEvent) => void;
  onDragEnd: () => void;
  onDragOver: (e: DragEvent) => void;
  onDragLeave: (e: DragEvent) => void;
  onDrop: (e: DragEvent) => void;
};

export type UseEingangReihenfolgeOptions = {
  /** Von außen (Rechteprüfung Admin/kaufm. Leitung) — der Hook entscheidet nicht selbst. */
  canReorder: boolean;
};

export type UseEingangReihenfolgeResult = {
  /**
   * Sortiert nach dem Index in der geladenen Reihenfolge. Projekte ohne Eintrag
   * (z. B. frisch gesynct, noch nie verschoben) werden hinten angehängt —
   * untereinander stabil nach Kunde, dann Projekt-Nummer.
   *
   * Das ist eine reine NOTSORTIERUNG fürs Anzeigen, solange die Server-Liste
   * (noch) nichts über das Projekt weiß — **nicht** die Fachregel. Die Fachregel
   * (Server, Task 3) setzt ein neues Projekt hinter das letzte Projekt desselben
   * Kunden; der Client kennt diese Zuordnung nicht und hängt stattdessen alle
   * unbekannten Projekte gemeinsam ans Ende. Sobald der Server das Projekt in
   * seine Liste aufgenommen hat, übernimmt dessen Reihenfolge.
   *
   * Die Constraint ist bewusst weiter gefasst als nur `{ project_id: string }`:
   * das Anhängen unsortierter Projekte braucht `customer_name`/`project_key` —
   * ohne sie bliebe nur eine `unknown`-Zugriffs-Turnerei, die die NIEMALS-`any`-
   * Regel genauso verletzen würde, nur unauffälliger. Jeder tatsächliche Aufrufer
   * übergibt `Project[]`, das beide Felder als Pflichtfelder trägt.
   */
  sortiere: <T extends { project_id: string; customer_name: string; project_key: string }>(
    items: T[],
  ) => T[];
  canReorder: boolean;
  dragId: string | null;
  /** Ziel-Projekt-Id oder `REIHENFOLGE_ENDE_ZIEL`. */
  dropTarget: string | null;
  handlers: (projectId: string) => EingangReihenfolgeHandlers;
  endeHandlers: { onDragOver: (e: DragEvent) => void; onDrop: (e: DragEvent) => void };
  zuruecksetzen: () => void;
  isPending: boolean;
  /**
   * Die Reihenfolge konnte nicht geladen werden — `sortiere` liefert dann still
   * die unsortierte Eingabe zurück (`items` bleibt `[]`). Ohne sichtbaren Hinweis
   * sähe die kaufmännische Leitung eine andere Reihenfolge als alle anderen,
   * ohne es zu merken. Die Seite zeigt dafür eine Zeile über der Liste.
   */
  isError: boolean;
};

const LEERE_HANDLER: EingangReihenfolgeHandlers = {
  draggable: false,
  onDragStart: () => {},
  onDragEnd: () => {},
  onDragOver: () => {},
  onDragLeave: () => {},
  onDrop: () => {},
};

export function useEingangReihenfolge({
  canReorder,
}: UseEingangReihenfolgeOptions): UseEingangReihenfolgeResult {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [dragId, setDragId] = useState<string | null>(null);
  const [dropTarget, setDropTarget] = useState<string | null>(null);

  const reihenfolgeQuery = useQuery({
    queryKey: UEBERGABE_REIHENFOLGE_QUERY_KEY,
    queryFn: api.getUebergabeReihenfolge,
  });
  const items = reihenfolgeQuery.data?.items ?? [];

  const verschiebenMut = useMutation({
    mutationFn: api.verschiebeUebergabeReihenfolge,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: UEBERGABE_REIHENFOLGE_QUERY_KEY });
    },
    onError: () => {
      toast.error(t('v2.handover.reihenfolge_fehler', 'Umsortieren fehlgeschlagen'));
    },
  });

  const zuruecksetzenMut = useMutation({
    mutationFn: api.resetUebergabeReihenfolge,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: UEBERGABE_REIHENFOLGE_QUERY_KEY });
    },
    onError: () => {
      toast.error(t('v2.handover.reihenfolge_zuruecksetzen_fehler', 'Zurücksetzen fehlgeschlagen'));
    },
  });

  function sortiere<T extends { project_id: string; customer_name: string; project_key: string }>(
    list: T[],
  ): T[] {
    const index = new Map(items.map((id, i) => [id, i] as const));
    return list
      .map((item, i) => ({ item, i }))
      .sort((a, b) => {
        const ia = index.get(a.item.project_id);
        const ib = index.get(b.item.project_id);
        if (ia !== undefined && ib !== undefined) return ia - ib;
        if (ia !== undefined) return -1;
        if (ib !== undefined) return 1;
        const byCustomer = a.item.customer_name.localeCompare(b.item.customer_name);
        if (byCustomer !== 0) return byCustomer;
        const byKey = a.item.project_key.localeCompare(b.item.project_key);
        // Array.prototype.sort ist in allen unterstützten Engines stabil (ES2019) —
        // trotzdem der Index als letzter Tie-Breaker, falls Kunde UND Nummer gleich sind.
        return byKey !== 0 ? byKey : a.i - b.i;
      })
      .map((x) => x.item);
  }

  /**
   * Zielprojekt steht in der aktuellen Liste bereits direkt hinter dem gezogenen Projekt (bzw.
   * ist beim Ablegen "ans Ende" bereits das letzte) — die Verschiebung wäre ein No-op. Ohne diese
   * Prüfung löste ein bestätigter, aber wirkungsloser Ablegeplatz trotzdem einen vollen
   * Schreibvorgang aus (Gesamt-Review #115, Befund 3).
   */
  function bewirktKeineAenderung(sourceId: string, zielProjectId: string | null): boolean {
    const i = items.indexOf(sourceId);
    if (i === -1) return false;
    const naechster = items[i + 1] ?? null;
    return naechster === zielProjectId;
  }

  function verschiebeAuf(zielProjectId: string | null): (e: DragEvent) => void {
    return (e) => {
      // Erst prüfen, ob der Drop überhaupt von einer Übergabe-Karte stammt — der Handler
      // hängt am ganzen Kartencontainer und würde sonst auch fremde Drops innerhalb der Karte
      // verschlucken (z. B. markierten Text in ein Notiz-Textfeld ziehen). `preventDefault`
      // erst NACH dieser Prüfung, sonst bliebe genau dieses Verschlucken bestehen
      // (Gesamt-Review #115, Befund 2).
      const sourceId = e.dataTransfer.getData(DND_MIME);
      if (!sourceId) return;
      e.preventDefault();
      setDropTarget(null);
      setDragId(null);
      if (sourceId === zielProjectId) return;
      if (bewirktKeineAenderung(sourceId, zielProjectId)) return;
      verschiebenMut.mutate({ project_id: sourceId, before_project_id: zielProjectId });
    };
  }

  function handlers(projectId: string): EingangReihenfolgeHandlers {
    if (!canReorder) return LEERE_HANDLER;
    return {
      draggable: true,
      onDragStart: (e) => {
        e.dataTransfer.setData(DND_MIME, projectId);
        e.dataTransfer.effectAllowed = 'move';
        setDragId(projectId);
      },
      onDragEnd: () => {
        setDragId(null);
        setDropTarget(null);
      },
      onDragOver: (e) => {
        if (!e.dataTransfer.types.includes(DND_MIME)) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        if (dropTarget !== projectId) setDropTarget(projectId);
      },
      onDragLeave: (e) => {
        if (e.currentTarget.contains(e.relatedTarget as Node)) return;
        if (dropTarget === projectId) setDropTarget(null);
      },
      onDrop: verschiebeAuf(projectId),
    };
  }

  const endeHandlers = {
    onDragOver: (e: DragEvent) => {
      if (!canReorder) return;
      if (!e.dataTransfer.types.includes(DND_MIME)) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
      if (dropTarget !== REIHENFOLGE_ENDE_ZIEL) setDropTarget(REIHENFOLGE_ENDE_ZIEL);
    },
    onDrop: canReorder
      ? verschiebeAuf(null)
      : (e: DragEvent) => {
          e.preventDefault();
        },
  };

  return {
    sortiere,
    canReorder,
    dragId,
    dropTarget,
    handlers,
    endeHandlers,
    zuruecksetzen: () => zuruecksetzenMut.mutate(),
    isPending: verschiebenMut.isPending || zuruecksetzenMut.isPending,
    isError: reihenfolgeQuery.isError,
  };
}
